// src/components/auth/AccountSetup.jsx
// Setting up a new account (Roadmap #88, registration confirm-first;
// docs/briefs/2026-10-03-registration-confirm-first.md R4–R7, R9 + addendum 1).
//
// Shown by App to a signed-in member with no home area — after confirming their email, or on any
// later visit from any device until it is done (R4). It cannot be dismissed; "Sign out" is always
// there. Steps: choose your location (home parish) → choose your area (RegisterLocationStep, both
// stages and the identity box) → an optional picture and About me. Nothing is written until the
// member acts; the home area is created when the area step is finished.
//
// Built on the modal shell like AccountReturnModal (the blocking-gate precedent): ESC and backdrop
// off, HelpButton and focus trap come with the shell. Each screen carries only its own short title
// and the step dots (R9). Buttons show they are working, then succeed or say what went wrong (R7).
//
// Keyed on the member by App (key={userId}), so nothing half-chosen survives an account switch.

import React, { useState } from 'react';
import Modal from '../core/Modal';
import StandardButton from '../core/StandardButton';
import ImageCropper from '../core/ImageCropper/ImageCropper';
import RegisterLocationStep from './RegisterLocationStep';
import { useTranslation } from '../../i18n/useTranslation';
import { createHomeAreaFromBoundary } from '../../utils/homeAreaSelfHealer';
import { updateOwnProfile } from '../../data/accountService';
import { resolveDefaultCountry } from '../../data/countriesService';
import { getCachedIpLocation } from '../../utils/ipGeolocation';
import { tokens } from '../../styles/designTokens';

const BIO_MAX_LENGTH = 1000;   // public.users.bio CHECK (length(bio) <= 1000)

const AccountSetup = ({ userId, onComplete, onSignOut }) => {
  const { t } = useTranslation('auth');
  const [step, setStep] = useState('place');          // 'place' | 'profile'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  // As the old registration did (the 2 Sep UK-signup fix): the country to open the map on.
  const countryCode = resolveDefaultCountry({ ipCountryCode: getCachedIpLocation()?.countryCode });

  const handlePlaceChosen = async ({ home, areaBoundaryIds, areaIdentity }) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    // A blank or whitespace-only name falls back to the parish's own (29 Aug Advisor finding,
    // carried over from the retired stash path).
    const name = areaIdentity?.displayName?.trim();
    try {
      const result = await createHomeAreaFromBoundary(userId, home.boundaryId, {
        isAutoDetected: false,
        boundaryIds: areaBoundaryIds,
        displayName: name || undefined,
        areaCode: areaIdentity?.areaCode,
        colorHex: areaIdentity?.colorHex
      });
      // 'home-now-exists': another tab finished first — the member has a home, which is the goal.
      if (result.created || result.reason === 'home-now-exists') {
        window.dispatchEvent(new CustomEvent('ournet:refresh-locations', { detail: { userId } }));
        setStep('profile');
      } else {
        setError(result.reason === 'code-collision-exhausted' ? 'setup_error_code_taken' : 'setup_error_place');
      }
    } catch (err) {
      console.error('Account setup: home area not created:', err);
      setError('setup_error_place');
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async () => {
    if (busy) return;
    const fields = {};
    if (bio.trim()) fields.bio = bio.trim();
    if (avatarUrl) fields.avatar_url = avatarUrl;
    if (Object.keys(fields).length === 0) { onComplete(); return; }
    setBusy(true);
    setError(null);
    const { error: saveError } = await updateOwnProfile(userId, fields);
    setBusy(false);
    if (saveError) {
      console.error('Account setup: profile not saved:', saveError);
      setError('setup_error_profile');
      return;
    }
    onComplete();
  };

  const dot = (on) => ({
    width: '8px', height: '8px', borderRadius: '9999px',
    background: on ? '#024B99' : tokens.colors.borderSecondary
  });
  const titleStyle = { margin: '0 0 12px 0', fontSize: '20px', fontWeight: 700, color: '#024B99', textAlign: 'center' };

  return (
    <>
      <Modal
        isOpen
        onClose={() => {}}
        closeOnBackdrop={false}
        closeOnEsc={false}
        alignTop
        panelStyle={{ backgroundColor: 'white', borderRadius: '24px', width: '100%', maxWidth: '600px', margin: '0 16px' }}
      >
        <div style={{ padding: '24px 24px 16px 24px', fontFamily: tokens.typography.fontFamily }}>
          <div className="flex justify-center gap-2 mb-3" aria-label={t('wizard_step_label', { step: step === 'place' ? 1 : 2, total: 2 })}>
            <div style={dot(true)} />
            <div style={dot(step === 'profile')} />
          </div>

          {error && (
            <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {t(error)}
            </div>
          )}

          {step === 'place' && (
            <>
              {busy && <p className="text-center text-sm mb-2" style={{ color: '#024B99' }}>{t('common:please_wait')}</p>}
              <RegisterLocationStep onComplete={handlePlaceChosen} onBack={null} countryCode={countryCode} />
            </>
          )}

          {step === 'profile' && (
            <div>
              <h2 style={titleStyle}>{t('setup_profile_title')}</h2>
              <p className="text-sm mb-4 text-center" style={{ color: '#024B99' }}>{t('setup_profile_question')}</p>

              <div className="flex items-center gap-4 mb-4">
                <div style={{
                  width: '72px', height: '72px', borderRadius: '9999px', overflow: 'hidden', flexShrink: 0,
                  background: tokens.colors.disabledBackground
                }}>
                  {avatarUrl && <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </div>
                <StandardButton
                  variant="utility"
                  label={avatarUrl ? t('setup_change_picture') : t('setup_add_picture')}
                  onClick={() => setShowCropper(true)}
                  disabled={busy}
                />
              </div>

              <label htmlFor="setup-bio" className="block text-sm font-medium mb-2" style={{ color: '#024B99' }}>
                {t('wizard_bio_label')}
              </label>
              <textarea
                id="setup-bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={BIO_MAX_LENGTH}
                placeholder={t('wizard_bio_placeholder')}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ fontSize: '14px', resize: 'vertical' }}
              />

              <div className="flex gap-3 mt-6 justify-center items-center flex-wrap">
                <StandardButton variant="ghost" label={t('wizard_skip')} onClick={onComplete} disabled={busy} />
                <StandardButton
                  variant="action"
                  label={busy ? t('common:please_wait') : t('setup_finish')}
                  onClick={handleFinish}
                  disabled={busy}
                />
              </div>
            </div>
          )}

          <div className="text-center text-sm mt-6">
            {/* Bare <button>, as the auth modal's own text links (LoginRegisterModal), fixed blue
                under the auth-surface colour exception — see the ledger (Architect question C1). */}
            <button
              type="button"
              onClick={onSignOut}
              className="hover:underline"
              style={{ color: '#024B99', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              {t('setup_sign_out')}
            </button>
          </div>
        </div>
      </Modal>

      <ImageCropper
        isOpen={showCropper}
        onClose={() => setShowCropper(false)}
        onSave={({ displayUrl }) => { setAvatarUrl(displayUrl); setShowCropper(false); }}
        aspectMode="inline"
        bucket="hero-images"
        userId={userId}
      />
    </>
  );
};

export default AccountSetup;
